import i18next from 'i18next';
import type { TOptions } from 'i18next';
import type { GameConfig } from '../config/types';
import type { BonusGameType } from '../constants/bonusConstants';
import type { AppVariant } from '../config/types';
import type { PackageType } from '../config/pricing';

/** Shorthand for translation outside React components. */
export function t(key: string, options?: TOptions): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return String((i18next.t as any)(key, options));
}

export function translateGame(game: GameConfig): GameConfig {
  const id = game.id;
  return {
    ...game,
    name: t(`games.${id}.name`),
    description: t(`games.${id}.description`),
    extendedDescription: t(`games.${id}.extendedDescription`),
  };
}

export function translateGames(games: GameConfig[]): GameConfig[] {
  return games.map(translateGame);
}

export function getGameDisplayName(gameType: string): string {
  let normalizedType = gameType;
  if (gameType.startsWith('team-')) {
    normalizedType = gameType.replace('team-', '');
  } else if (gameType.startsWith('family-')) {
    normalizedType = gameType.replace('family-', '');
  }

  const key = `games.${normalizedType}.name`;
  const translated = t(key);
  if (translated !== key) {
    return translated;
  }

  return normalizedType
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getCategoryLabel(value: string): string {
  const key = `categories.${value}`;
  const translated = t(key);
  return translated !== key ? translated : value;
}

export function getPackHeaderTranslated(variant: string): { name: string; phrase: string } {
  const key = ['blitz', 'team', 'family', 'party', 'coop'].includes(variant) ? variant : 'coop';
  return {
    name: t(`lobby.packs.${key}.name`),
    phrase: t(`lobby.packs.${key}.phrase`),
  };
}

export function getGameLinkName(id: string): string {
  const key = `lobby.gameLinks.${id}`;
  const translated = t(key);
  return translated !== key ? translated : id;
}

export function getBonusIntro(type: BonusGameType): { title: string; description: string } {
  return {
    title: t(`bonus.intro.${type}.title`),
    description: t(`bonus.intro.${type}.description`),
  };
}

export function getBonusNoWinnerMessages(): Array<{ title: string; subtitle: string }> {
  const keys = [
    'timesUp', 'unsolved', 'nobodyWon', 'roundOver', 'timeRanOut',
    'barOnFloor', 'congratsNobody', 'something',
  ] as const;
  return keys.map((k) => ({
    title: t(`bonus.noWinner.${k}.title`),
    subtitle: t(`bonus.noWinner.${k}.subtitle`),
  }));
}

export function getBonusChainCompleteMessages(): Array<{ title: string; subtitle: string }> {
  const keys = [
    'wellDone', 'awesome', 'youAreAwesome', 'mathMachine', 'nailedIt',
    'legend', 'speedDemon', 'newPythagoras', 'geniusMode', 'cleanSweep',
    'sharp', 'brilliance',
  ] as const;
  return keys.map((k) => ({
    title: t(`bonus.chainComplete.${k}.title`),
    subtitle: t(`bonus.chainComplete.${k}.subtitle`),
  }));
}

export function getPricingPackageName(packageId: PackageType): string {
  return t(`payment.packages.${packageId}.name`);
}

export function getPricingPackageDescription(packageId: PackageType): string {
  return t(`payment.packages.${packageId}.description`);
}

export function getSeoForVariant(variant: AppVariant) {
  return {
    title: t(`seo.${variant}.title`),
    description: t(`seo.${variant}.description`),
    ogTitle: t(`seo.${variant}.ogTitle`),
    ogDescription: t(`seo.${variant}.ogDescription`),
    twitterTitle: t(`seo.${variant}.twitterTitle`),
    twitterDescription: t(`seo.${variant}.twitterDescription`),
  };
}

export function formatQuestionLabel(
  questionNumber?: number,
  totalQuestions?: number
): string {
  const hasNumbers = typeof questionNumber === 'number' && typeof totalQuestions === 'number';
  if (hasNumbers) {
    return t('gameplay.questionProgress', { current: questionNumber, total: totalQuestions });
  }
  return t('gameplay.questionProgressUnknown');
}

export const JOIN_ROOM_NOT_FOUND_MESSAGE = () => t('lobby.join.roomNotFound');

/** Map known backend error messages to translation keys. Falls back to raw message. */
export function translateBackendError(message: string): string {
  const lower = message.toLowerCase();
  if (
    lower.includes("room doesn't exist") ||
    lower.includes('room not found') ||
    /room\s+"[^"]+"\s+not found/.test(lower) ||
    /room\s+'[^']+'\s+not found/.test(lower) ||
    lower.includes('room destroyed')
  ) {
    return t('toasts.roomNotExist');
  }
  if (lower.includes('already in room') || lower.includes('already in the room')) {
    return t('toasts.duplicatePlayerName');
  }
  if (lower.includes('host already using')) {
    return t('toasts.hostAlreadyUsingRoom');
  }
  if (lower.includes('admin room in use')) {
    return t('toasts.adminRoomInUse');
  }
  if (lower.includes('that name is already taken') || lower.includes('name is already taken')) {
    return t('lobby.join.duplicateName');
  }
  return message;
}
